<template>
  <div class="approval-config-info">
    <KTCodePreview v-bind:title="'Thông tin cấu hình duyệt chi'">
      <template v-slot:preview>
        <div v-if="loading">
          <div class="d-flex justify-content-center">
            <div class="spinner spinner-primary spinner-lg"></div>
          </div>
        </div>
        
        <div v-else>
          <approval-config-form
            v-model="formData"
            :type-options="typeOptions"
          />
          
          <approval-config-detail-table
            v-model="formData.details"
            :disabled="formData.disabled"
            @show-owner-modal="showOwnerModal"
            @delete-detail="showDeleteAlert"
            v-if="renderComponent"
          />
          
          <action-buttons
            :show-save-button="isShowSubmit()"
            @save="onSubmit"
            @cancel="onBack"
          />
          
          <approval-owner-modal
            ref="approval-owner-modal"
            @save="saveConfigDetail"
            :mode="formData.mode"
            v-if="renderComponent"
          />
        </div>
      </template>
    </KTCodePreview>
  </div>
</template>

<script>
import KTCodePreview from '@/view/content/CodePreview.vue';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import Swal from 'sweetalert2';
import reject from 'lodash/reject';
import { makeToastFaile } from '@/utils/common';
import { APPROVAL_TYPE } from '@/utils/constants';
import { checkPermission } from '@/utils/saveDataToLocal';
import ApprovalConfigService from '../services/ApprovalConfigService';

// Import components
import {
  ApprovalConfigForm,
  ApprovalConfigDetailTable,
  ApprovalOwnerModal,
  ActionButtons
} from './components';

export default {
  name: 'ApprovalConfigInfo',
  components: {
    KTCodePreview,
    ApprovalConfigForm,
    ApprovalConfigDetailTable,
    ApprovalOwnerModal,
    ActionButtons
  },
  data() {
    return {
      loading: false,
      submitting: false,
      renderComponent: false,
      typeOptions: APPROVAL_TYPE,
      formData: {
        id: null,
        name: '',
        approvalType: 1,
        numOfStep: 1,
        isActive: 1,
        details: [],
        mode: 'create',
        disabled: false,
      }
    };
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: 'Cấu hình duyệt chi', route: '/approval-configs' },
      { title: 'Thông tin cấu hình duyệt chi' },
    ]);
    
    this.initializeForm();
  },
  methods: {
    checkPermission,
    
    /**
     * Initialize the form based on route params
     */
    initializeForm() {
      if (this.$route.query.id) {
        this.formData.id = this.$route.query.id;
        this.formData.mode = 'update';
        this.getInfoById();
      } else {
        this.renderComponent = true;
      }
    },
    
    /**
     * Get approval config by ID
     */
    async getInfoById() {
      this.loading = true;
      
      try {
        const configData = await ApprovalConfigService.getApprovalConfigById(this.formData.id);
        this.formData = { ...configData };
        
        if (!this.formData.disabled) {
          this.formData.disabled = !this.checkPermission('APPROVAL_CONFIG_UPDATE');
        }
      } catch (error) {
        console.error('Error fetching approval config:', error);
      } finally {
        this.renderComponent = true;
        this.loading = false;
      }
    },
    
    /**
     * Show the owner modal for a config detail
     */
    showOwnerModal(configDetail) {
      this.$refs['approval-owner-modal'].showModal(configDetail);
    },
    
    /**
     * Save the config detail from the modal
     */
    saveConfigDetail(configDetail) {
      const foundedIndexDetail = this.formData.details.findIndex(
        (detail) => detail.id === configDetail.id
      );
      
      if (foundedIndexDetail < 0) {
        return makeToastFaile('Điều kiện duyệt không tồn tại!');
      }
      
      this.formData.details[foundedIndexDetail].ownerType = configDetail.ownerType;
      this.formData.details[foundedIndexDetail].owners = configDetail.owners;
      
      const ownerNames = configDetail.owners.map((owner) => owner.name);
      this.formData.details[foundedIndexDetail].ownerNames = `( ${ownerNames.join(', ')} )`;
    },
    
    /**
     * Show delete confirmation for a config detail
     */
    showDeleteAlert(item) {
      Swal.fire({
        title: 'Xóa chi tiết!',
        text: 'Bạn có chắc muốn xóa chi tiết này không ?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: 'rgb(221, 51, 51)',
        cancelButtonColor: '#E5EAEE',
        confirmButtonText: 'Xóa',
        cancelButtonText: '<span style="color:#464E5F">Hủy</span>',
        reverseButtons: true,
        showClass: {
          popup: 'animate__animated animate__fadeInDown',
        },
        hideClass: {
          popup: 'animate__animated animate__fadeOutUp',
        },
        position: 'top',
      }).then((result) => {
        if (result.dismiss != 'cancel') {
          this.formData.details = reject(this.formData.details, (detail) => {
            return detail.id == item.id;
          });
        }
      });
    },
    
    /**
     * Submit the form
     */
    async onSubmit() {
      if (this.submitting) {
        return;
      }
      
      // Basic validation
      if (!this.formData.name) {
        return makeToastFaile('Vui lòng nhập tên cấu hình duyệt chi!');
      }
      
      if (!this.formData.approvalType) {
        return makeToastFaile('Vui lòng chọn loại cấu hình duyệt chi!');
      }
      
      if (!this.formData.numOfStep || this.formData.numOfStep < 1) {
        return makeToastFaile('Vui lòng nhập số bước duyệt chi hợp lệ!');
      }
      
      // Prepare payload
      const payload = ApprovalConfigService.prepareConfigPayload(this.formData);
      
      // Validate details
      const isValid = ApprovalConfigService.validateConfigDetails(
        payload.details,
        payload.numOfStep
      );
      
      if (!isValid) {
        return;
      }
      
      // Submit form
      this.submitting = true;
      
      try {
        const result = await ApprovalConfigService.saveApprovalConfig(
          payload,
          !!this.formData.id
        );
        
        if (result.success) {
          this.$router.push({
            path: '/approval-configs',
          });
        }
      } catch (error) {
        console.error('Error saving approval config:', error);
      } finally {
        this.submitting = false;
      }
    },
    
    /**
     * Navigate back to the list
     */
    onBack() {
      this.$router.push({
        name: 'list-approval-config',
      });
    },
    
    /**
     * Check if the submit button should be shown
     */
    isShowSubmit() {
      if (this.formData.id) {
        return this.checkPermission('APPROVAL_CONFIG_UPDATE');
      }
      return this.checkPermission('APPROVAL_CONFIG_INSERT');
    },
  },
};
</script>

<style lang="scss" scoped>
.approval-config-info {
  h4 {
    margin-top: 1.5rem;
    margin-bottom: 1rem;
  }
}
</style>
